import { getQueryFn, apiRequest, queryClient } from './queryClient';
import { User } from '@shared/schema';

/**
 * Interface for the token response from the token generation endpoint
 */
interface TokenResponse {
  token: string;
}

/**
 * Generate an authentication token for sharing between applications
 * @returns A promise that resolves to the JWT token
 */
export async function generateSharedAuthToken(): Promise<string | null> {
  try {
    const response = await apiRequest<TokenResponse>('GET', '/api/auth/token');

    return response.token;
  } catch (error) {
    console.error('Failed to generate shared auth token:', error);
    return null;
  }
}

/**
 * Type definition for user data without the password field
 * This is what gets returned from token verification
 */
export type UserWithoutPassword = {
  id: number;
  username: string;
};

/**
 * Verify an authentication token received from another application
 * @param token The JWT token to verify
 * @returns A promise that resolves to the user object if valid, null if invalid
 */
export async function verifySharedAuthToken(token: string): Promise<UserWithoutPassword | null> {
  try {
    const response = await apiRequest<UserWithoutPassword>('POST', '/api/auth/verify-token', {
      token,
    });

    return response;
  } catch (error) {
    console.error('Failed to verify shared auth token:', error);
    return null;
  }
}

/**
 * Store the auth token in local storage
 * @param token The JWT token to store
 */
export function storeAuthToken(token: string): void {
  localStorage.setItem('sharedAuthToken', token);
}

/**
 * Retrieve the auth token from local storage
 * @returns The stored JWT token or null if not found
 */
export function retrieveAuthToken(): string | null {
  return localStorage.getItem('sharedAuthToken');
}

/**
 * Clear the auth token from local storage
 */
export function clearAuthToken(): void {
  localStorage.removeItem('sharedAuthToken');
}
